import { useScrollReveal } from '@/hooks/useScrollReveal';
import SlotText from '@/components/feature/SlotText';


const expertiseDomains = [
  {
    no: '01',
    en: 'Brand Strategy',
    ja: 'ブランド戦略',
    detail: 'ブランド定義・ポジショニング・価値設計・VI開発・展開戦略',
  },
  {
    no: '02',
    en: 'Business Design',
    ja: 'ビジネスデザイン',
    detail: '事業課題整理・価値提案・サービス構想・KGI/KPI設計',
  },
  {
    no: '03',
    en: 'CX Planning',
    ja: 'CXプランニング',
    detail: 'CX・UX・サービス体験・顧客接点・導線設計・実装設計',
  },
  {
    no: '04',
    en: 'Communication Design',
    ja: 'コミュニケーション設計',
    detail: '統合コミュニケーション戦略・メッセージ設計・メディアプランニング',
  },
  {
    no: '05',
    en: 'Creative Direction',
    ja: 'クリエイティブディレクション',
    detail: 'ビジュアル設計・コンテンツ制作・品質管理・制作進行',
  },
];

const designPhilosophy = [
  {
    title: '課題の本質から出発する',
    desc: '表層の症状ではなく、その背後にある本質的な問題を発見することから始める。問いを正しく立てることが、解のクオリティを決定する。',
  },
  {
    title: '戦略とクリエイティブを統合する',
    desc: '戦略なきクリエイティブは装飾であり、クリエイティブなき戦略は機能しない。両者を不可分なものとして設計する。',
  },
  {
    title: '生活者視点を忘れない',
    desc: 'すべての設計判断は、最終的にそれを受け取る人間の視点から検証される。ユーザーの文脈と感情を常に基準に置く。',
  },
  {
    title: '美しさと機能を両立させる',
    desc: '美しいものは機能し、機能するものは美しい。審美性と実用性を対立概念ではなく、統合すべき一体の価値として捉える。',
  },
];

const awards = [
  {
    year: '2021',
    name: 'グッドデザイン賞',
    detail: 'BABY&Me BELK. ネーミング・ロゴ・パッケージブランディング',
    org: '公益財団法人日本デザイン振興会',
  },
  {
    year: '2021',
    name: 'キッズデザイン賞',
    detail: 'BABY&Me BELK. ネーミング・ロゴ・パッケージブランディング',
    org: '特定非営利活動法人キッズデザイン協議会',
  },
  {
    year: '2022',
    name: 'ascon 社内アワード 金賞',
    detail: '最も活躍された方に贈られる個人アワード',
    org: '株式会社ascon',
  },
  {
    year: '大学院',
    name: 'グラフィックデザイン研究室賞',
    detail: '動的な環境認知に及ぼすアフォーダンスデザイン手法研究',
    org: '多摩美術大学大学院',
  },
];

export default function About() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section id="profile" className="py-20 md:py-32 bg-background-50">
      <div className="mx-auto max-w-[1280px] px-6 md:px-10">

        {/* ─── SECTION HEADER ─── */}
        <div
          ref={ref}
          className={`mb-12 transition-all duration-1000 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
            <span className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500">
              About / Profile
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-6">
              <div className="relative inline-block">
                {/* Rainbow band behind the headline */}
                <div
                  className="absolute -inset-x-2 top-[55%] -translate-y-1/2 h-[0.65em] -z-10 rounded-full overflow-hidden"
                  style={{
                    backgroundImage: `linear-gradient(
                      90deg,
                      oklch(0.82 0.17 20 / 0) 0%,
                      oklch(0.84 0.16 55 / 0.85) 14%,
                      oklch(0.80 0.15 115 / 0.95) 36%,
                      oklch(0.76 0.14 175 / 0.95) 58%,
                      oklch(0.73 0.16 240 / 0.88) 80%,
                      oklch(0.76 0.19 305 / 0.68) 95%,
                      oklch(0.76 0.19 305 / 0) 100%
                    )`,
                  }}
                />
                <h2 className="font-heading font-bold text-[clamp(32px,4.5vw,58px)] text-foreground-950 leading-[0.93] tracking-[-0.04em]">
                  <SlotText text="Design is not how" as="span" />
                  <br />
                  <SlotText text="it looks." as="span" className="text-foreground-400 font-medium" />
                </h2>
              </div>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-foreground-600 text-sm leading-relaxed">
                課題の本質から出発し、戦略とクリエイティブをひとつの力に統合する。
                20年以上のキャリアは、この一本の軸でつながっています。
              </p>
            </div>
          </div>
        </div>

        {/* ─── PROFILE BLOCK ─── */}
        <div
          className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-20 transition-all duration-1000 delay-100 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Photos — Clean collage gallery */}
          {/* ─── PHOTO GALLERY ─── */}
          <div className="md:col-span-5 self-center">
            <div className="border border-background-300 rounded-xl overflow-hidden bg-background-100 p-2">
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                <div className="overflow-hidden rounded-lg aspect-[4/3]">
                  <img
                    src="/assets/migrated/a926544d284a66125122.webp"
                    alt="中津圭司 — レゴの人形と"
                    className="w-full h-full object-cover object-center grayscale hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="overflow-hidden rounded-lg aspect-[4/3]">
                  <img
                    src="/assets/migrated/1b6d70f0191c01526824.webp"
                    alt="中津圭司 — クリスマスと愛犬"
                    className="w-full h-full object-cover object-center grayscale hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="overflow-hidden rounded-lg aspect-[4/3]">
                  <img
                    src="/assets/migrated/16b0d6a82def40b404be.webp"
                    alt="中津圭司 — レストランで"
                    className="w-full h-full object-cover object-center grayscale hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="overflow-hidden rounded-lg aspect-[4/3]">
                  <img
                    src="/assets/migrated/125ac644b785ec980c6f.webp"
                    alt="中津圭司 — 愛犬たち"
                    className="w-full h-full object-cover object-center grayscale hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="overflow-hidden rounded-lg aspect-[4/3]">
                  <img
                    src="/assets/migrated/a1cfbb6f452c74af663e.webp"
                    alt="中津圭司 — 壁画の前で"
                    className="w-full h-full object-cover object-center grayscale hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="overflow-hidden rounded-lg aspect-[4/3]">
                  <img
                    src="/assets/migrated/df7ecb05d5b54cd1fc58.webp"
                    alt="中津圭司 — クリスマスの愛犬たち"
                    className="w-full h-full object-cover object-center grayscale hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
            </div>

            {/* Photo captions */}
            <div className="mt-4 flex items-start gap-3">
              <span className="w-6 h-px bg-foreground-300 mt-2 shrink-0" />
              <p className="text-[10px] text-foreground-400 leading-relaxed">
                プロフェッショナルの鋭さ、クリエイターの遊び心、人間らしい温かみ——三つの側面を大切にしています。
              </p>
            </div>
          </div>

          {/* Info */}
          <div className="md:col-span-7">
            {/* Identity */}
            <div className="border-t-2 border-foreground-950 pt-6 mb-8">
              <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500 mb-5">
                Professional Identity
              </p>
              <div className="mb-5">
                <SlotText
                  text="KEIJI NAKATSU"
                  as="p"
                  className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 tracking-[-0.03em]"
                />
                <p className="font-label text-[10px] tracking-[0.1em] uppercase text-foreground-400 mt-1">
                  <SlotText text="中津 圭司" />
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <p className="font-label text-[9px] tracking-[0.1em] uppercase text-foreground-400 mb-1">
                    Primary Role
                  </p>
                  <p className="text-sm text-foreground-900 font-medium">
                    CX &amp; COMMUNICATION STRATEGIST
                  </p>
                </div>
                <div>
                  <p className="font-label text-[9px] tracking-[0.1em] uppercase text-foreground-400 mb-1">
                    Also Known As
                  </p>
                  <p className="text-sm text-foreground-700">
                    Creative Director / Communication Planner
                  </p>
                </div>
                <div>
                  <p className="font-label text-[9px] tracking-[0.1em] uppercase text-foreground-400 mb-1">
                    Experience
                  </p>
                  <p className="text-sm text-foreground-900">
                    20+ Years — 5 Companies — 100+ Projects
                  </p>
                </div>
                <div>
                  <p className="font-label text-[9px] tracking-[0.1em] uppercase text-foreground-400 mb-1">
                    Location
                  </p>
                  <p className="text-sm text-foreground-900">
                    Tokyo, Japan — Working Nationwide
                  </p>
                </div>
              </div>
            </div>

            {/* Profile Summary */}
            <div className="border-t border-background-300 pt-6 mb-8">
              <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500 mb-4">
                Profile Summary
              </p>
              <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                空間・サイン、リテール、パッケージ、広告、ブランド、デジタル、サービス領域を横断し、20年以上の実務経験を持つデザインストラテジスト。生活者・顧客視点に基づく課題整理から、マーケティング分析、戦略立案、企画書作成、クリエイティブディレクション、プレゼンテーション、制作・実装まで一貫して推進できることを強みとする。
              </p>
              <p className="text-sm text-foreground-700 leading-relaxed">
                課題解決手段であると同時に、人の心を動かし、未来をより良くする力として——デザインを捉えています。企画書をゼロから自ら構成し、プレゼン・スピーカーまで一貫して担う「ハイブリッド型」プロフェッショナルです。
              </p>
            </div>

            {/* Stats */}
            <div className="border-t border-background-300 pt-6 grid grid-cols-3 gap-4">
              {[
                { k: '20+', v: 'Years', sub: 'キャリア年数' },
                { k: '100+', v: 'Projects', sub: '担当プロジェクト数' },
                { k: '30+', v: 'Clients', sub: 'クライアント数' },
              ].map((s) => (
                <div key={s.v}>
                  <SlotText
                    text={s.k}
                    as="p"
                    className="font-heading text-[clamp(24px,3vw,42px)] text-foreground-950 leading-none tracking-[-0.04em]"
                  />
                  <p className="font-label text-[9px] tracking-[0.1em] uppercase text-primary-500 mt-1">
                    {s.v}
                  </p>
                  <p className="text-xs text-foreground-400 mt-0.5 hidden md:block">{s.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ─── DIGITAL SIGN ─── */}
        <div
          className={`mb-20 overflow-hidden transition-all duration-1000 delay-150 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="py-6 md:py-8 border-y border-background-300 relative">
            {/* Colorful gradient line top */}
            <div className="absolute top-0 left-0 right-0 h-1 overflow-hidden">
              <div
                className="w-full h-full"
                style={{
                  backgroundImage: `linear-gradient(
                    90deg,
                    oklch(0.82 0.17 20 / 0) 0%,
                    oklch(0.84 0.16 55 / 0.85) 14%,
                    oklch(0.80 0.15 115 / 0.95) 36%,
                    oklch(0.76 0.14 175 / 0.95) 58%,
                    oklch(0.73 0.16 240 / 0.88) 80%,
                    oklch(0.76 0.19 305 / 0.68) 95%,
                    oklch(0.76 0.19 305 / 0) 100%
                  )`,
                }}
              />
            </div>
            {/* Scrolling digital sign */}
            <div className="flex animate-marquee whitespace-nowrap">
              {[
                'Design is not how it looks.',
                'Design is how it works.',
                'CX & Communication Strategist',
                'Strategy to Implementation.',
                'Design is not how it looks.',
                'Design is how it works.',
                'CX & Communication Strategist',
                'Strategy to Implementation.',
              ].map((word, i) => (
                <span
                  key={i}
                  className="font-heading text-xl md:text-2xl text-foreground-950 mx-8 flex items-center tracking-[-0.02em]"
                >
                  <SlotText text={word} as="span" scrambleColor="rainbow" />
                  <span className="ml-8 w-1.5 h-1.5 rounded-full bg-accent-500 inline-flex items-center justify-center" />
                </span>
              ))}
            </div>
            {/* Colorful gradient line bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden">
              <div
                className="w-full h-full"
                style={{
                  backgroundImage: `linear-gradient(
                    90deg,
                    oklch(0.76 0.19 305 / 0) 0%,
                    oklch(0.76 0.19 305 / 0.68) 5%,
                    oklch(0.73 0.16 240 / 0.88) 20%,
                    oklch(0.76 0.14 175 / 0.95) 42%,
                    oklch(0.80 0.15 115 / 0.95) 64%,
                    oklch(0.84 0.16 55 / 0.85) 86%,
                    oklch(0.82 0.17 20 / 0) 100%
                  )`,
                }}
              />
            </div>
          </div>
        </div>

        {/* ─── EXPERTISE DIAGRAM ─── */}
        <div
          className={`mb-20 transition-all duration-1000 delay-200 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center gap-2 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
            <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500">
              Expertise Framework — Strategy to Business Value
            </p>
          </div>

          {/* Diagram: 5 domains in horizontal bar layout */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-px bg-background-200 border border-background-200 overflow-hidden">
            {expertiseDomains.map((d) => (
              <div
                key={d.no}
                className="bg-background-50 p-5 group hover:bg-primary-500 transition-colors duration-300 cursor-default"
              >
                <p className="font-label text-[9px] tracking-[0.1em] uppercase text-foreground-400 group-hover:text-foreground-950/70 mb-3 transition-colors">
                  {d.no}
                </p>
                <p className="font-heading font-bold text-xs md:text-sm text-foreground-950 group-hover:text-foreground-950 tracking-[-0.01em] mb-1 transition-colors">
                  {d.en}
                </p>
                <p className="font-label text-[10px] text-foreground-500 group-hover:text-foreground-950/80 mb-3 transition-colors">
                  {d.ja}
                </p>
                <p className="text-[10px] text-foreground-400 group-hover:text-foreground-950/70 leading-relaxed transition-colors">
                  {d.detail}
                </p>
              </div>
            ))}
          </div>

          {/* Bottom caption */}
          <p className="mt-4 text-xs text-foreground-400 text-center">
            課題発見から価値創出まで、すべてのフェーズを一貫して担当できることが強み
          </p>
        </div>

        {/* ─── DESIGN PHILOSOPHY ─── */}
        <div
          className={`mb-20 transition-all duration-1000 delay-300 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center gap-2 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-secondary-500" />
            <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500">
              Design Philosophy
            </p>
          </div>
          <div className="mb-8">
            <p className="font-heading font-bold text-[clamp(28px,4.5vw,52px)] text-foreground-950 leading-[1.05] tracking-[-0.03em]">
              Design is not how it looks.<br />
              <span className="text-accent-600">Design is <span className="marker-yellow text-foreground-950">how it works</span> — and why it matters.</span>
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-background-200 border border-background-200 overflow-hidden">
            {designPhilosophy.map((p, i) => (
              <div key={i} className="bg-background-50 p-6 md:p-8">
                <p className="font-label text-[9px] tracking-[0.1em] uppercase text-foreground-400 mb-2">
                  {String(i + 1).padStart(2, '0')}
                </p>
                <p className="font-heading font-bold text-base md:text-lg text-foreground-950 mb-2 tracking-[-0.01em]">
                  <SlotText text={p.title} as="span" />
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ─── BACKGROUND ─── */}
        <div
          className={`mb-20 transition-all duration-1000 delay-400 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center gap-2 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-foreground-400" />
            <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500">
              Background &amp; Education
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="border-t border-background-300 pt-6">
              <p className="font-label text-[9px] tracking-[0.1em] uppercase text-foreground-400 mb-6">
                Academic Background
              </p>
              <p className="font-heading font-bold text-xl md:text-[28px] text-foreground-950 tracking-[-0.02em] mb-6 leading-tight">
                <SlotText text="多摩美術大学 大学院 デザイン専攻" as="span" />
                <br />
                <SlotText text="グラフィックデザイン コース 修了" as="span" className="marker-yellow" color="oklch(0.09 0.01 70)" scrambleColor="rainbow" />
              </p>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                「動的な環境認知に及ぼすアフォーダンスデザイン手法研究」を専門とし、SDA日本サインデザイン協会で研究発表を行い、グラフィックデザイン研究室賞を受賞。
              </p>
            </div>
            <div className="border-t border-background-300 pt-6">
              <p className="font-label text-[9px] tracking-[0.1em] uppercase text-foreground-400 mb-3">
                Personal
              </p>
              <p className="text-xs text-foreground-600 leading-relaxed mb-4">
                実家が書道道具会社を営んでおり、幼少期から美術品に囲まれて過ごす。「人がどう見て、どう理解するか」という問いは、アフォーダンス研究を経て、現在のCX・ブランド戦略の思考の原点にある。
              </p>
              <p className="text-xs text-foreground-600 leading-relaxed mb-4">
                プライベートでは2匹の愛犬と東京で暮らしています。動物や自然が好きで、休日は犬との散歩や旅行を楽しんでいます。気になったことをすぐ調べる好奇心は、仕事でのリサーチや新しい領域への関心にもつながっています。
              </p>
              <p className="font-label text-[9px] tracking-[0.1em] uppercase text-foreground-400 mb-2">
                Tools
              </p>
              <p className="text-xs text-foreground-500 leading-relaxed">
                Adobe CC (Illustrator / Photoshop / InDesign / XD / Lightroom / Premiere) / Figma / PowerPoint / Microsoft 365 Office for Mac / Final Cut Pro X
              </p>
            </div>
          </div>
        </div>

        {/* ─── AWARDS ─── */}
        <div
          className={`transition-all duration-1000 delay-500 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center gap-2 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
            <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500">
              Awards &amp; Recognition
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {awards.map((a, i) => (
              <div
                key={i}
                className="border border-background-200 p-6 md:p-8 bg-background-50"
              >
                <span className="font-label text-[9px] tracking-[0.1em] uppercase text-foreground-400 block mb-3">
                  {a.year}
                </span>
                <SlotText
                  text={a.name}
                  as="p"
                  className="font-heading font-bold text-lg md:text-xl text-foreground-950 mb-2 tracking-[-0.02em] leading-tight"
                />
                <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                  {a.detail}
                </p>
                <p className="font-label text-[9px] tracking-[0.1em] uppercase text-foreground-400">
                  {a.org}
                </p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}